import React from "react";
import { Typography, List, ListItem, Container, Box } from "@mui/material";
import CustomHelmet from "../../common/CustomHelmet";

const AutismSupportForAdults = () => {
  return (
    <>
      <CustomHelmet
        title="Adult Autism Support Services - Dr. Anne Rothenberg"
        description="Dr. Anne Rothenberg provides neurodiversity-affirming support for autistic adults. Navigate late diagnosis, masking, burnout, and build authentic connections in a world that understands."
        keywords="adult autism support, autistic burnout, autism masking, late autism diagnosis, neurodiversity-affirming therapy, Dr. Anne Rothenberg"
        ogTitle="Neurodiversity-Affirming Support for Autistic Adults - Dr. Anne Rothenberg"
        ogDescription="Find understanding and support for late diagnosis, masking, burnout, and living authentically as an autistic adult. Dr. Anne Rothenberg provides affirming therapy that honors your neurodivergent experience."
        ogUrl="https://drannerothenberg.com/services/autism-support-for-adults"
      />
      <Container maxWidth="md">
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "left",
            textAlign: "left",
            padding: "20px",
            borderRadius: "8px",
          }}
        >
          <Typography variant="h4" gutterBottom>
            Support for Autistic Adults: You're Not Broken, You're Different
          </Typography>

          <Typography
            variant="subtitle1"
            gutterBottom
            sx={{ color: "#555", mb: 3 }}
          >
            Whether you've recently discovered you're autistic or have known for
            years, Dr. Anne Rothenberg provides neurodiversity-affirming support
            that honors your authentic self. Here, you don't need to mask,
            perform, or pretend to be neurotypical. This is a space where being
            autistic is understood, respected, and valued.
          </Typography>

          <Typography
            variant="body1"
            sx={{
              mb: { xs: 2, md: 3 },
              fontStyle: "italic",
              color: "#666",
              fontSize: { xs: "0.9rem", md: "1rem" },
              padding: { xs: "12px", md: "16px" },
              backgroundColor: "rgba(228, 181, 162, 0.05)",
              borderRadius: "8px",
              borderLeft: "4px solid #E4B5A2",
            }}
          >
            "For so many of us, autism isn't the problem - it's the exhaustion
            of trying to fit into a world that wasn't designed for us. You
            deserve support that sees autism as a neurological difference, not a
            disorder to be fixed."
          </Typography>

          <Typography
            variant="h6"
            gutterBottom
            sx={{ color: "#4A90E2", mt: 3 }}
          >
            "Nobody Understands How Hard This Is" - The Hidden Struggles
          </Typography>
          <List
            dense
            sx={{
              listStyleType: "disc",
              pl: 4,
              color: "#333",
              textAlign: "left",
              mb: 3,
            }}
          >
            <ListItem sx={{ display: "list-item" }}>
              "I've been pretending to be 'normal' my whole life and I'm
              exhausted"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "Small talk feels like speaking a foreign language"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "I can 'pass' at work but collapse when I get home"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "Eye contact is painful but I force myself to do it"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "I rehearse every conversation before it happens"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "The lights, sounds, and smells are overwhelming but no one
              notices"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "I've lost myself trying to be what everyone expects"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "I'm always the one who's 'too sensitive' or 'overthinking'"
            </ListItem>
          </List>

          <Typography
            variant="h6"
            gutterBottom
            sx={{ color: "#4A90E2", mt: 3 }}
          >
            Autistic Burnout: When You Can't Mask Anymore
          </Typography>
          <List
            dense
            sx={{
              listStyleType: "disc",
              pl: 4,
              color: "#333",
              textAlign: "left",
              mb: 3,
            }}
          >
            <ListItem sx={{ display: "list-item" }}>
              "I literally can't speak when I'm overwhelmed"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "Simple tasks like brushing my teeth feel impossible"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "I've lost skills I used to have - I can't cook anymore"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "Everything is too loud, too bright, too much"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "I need days to recover from one social event"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "I quit my job because I couldn't pretend anymore"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "My tolerance for anything is at zero"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "I feel like I'm falling apart"
            </ListItem>
          </List>

          <Typography
            variant="h6"
            gutterBottom
            sx={{ color: "#4A90E2", mt: 3 }}
          >
            Late Diagnosis: "Everything Finally Makes Sense"
          </Typography>
          <List
            dense
            sx={{
              listStyleType: "disc",
              pl: 4,
              color: "#333",
              textAlign: "left",
              mb: 3,
            }}
          >
            <ListItem sx={{ display: "list-item" }}>
              "I'm grieving the support I never got as a child"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "I'm angry that no one noticed I was struggling"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "My whole life story needs rewriting with this new lens"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "I don't know who I really am under all the masking"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "People don't believe me - 'you don't look autistic'"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "I'm relieved but also overwhelmed by what this means"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "How do I explain this to family who don't understand?"
            </ListItem>
          </List>

          <Typography
            variant="h6"
            gutterBottom
            sx={{ color: "#4A90E2", mt: 3 }}
          >
            Work and Daily Life Challenges
          </Typography>
          <List
            dense
            sx={{
              listStyleType: "disc",
              pl: 4,
              color: "#333",
              textAlign: "left",
              mb: 3,
            }}
          >
            <ListItem sx={{ display: "list-item" }}>
              "Open offices are sensory hell"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "Networking events make me want to hide"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "I need clear instructions but everyone speaks in hints"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "Sudden schedule changes derail my entire day"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "I'm great at my job but terrible at office politics"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "Phone calls are torture - I need time to process"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "Team building activities are my nightmare"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "I need accommodations but I'm scared to ask"
            </ListItem>
          </List>

          <Typography
            variant="h6"
            gutterBottom
            sx={{ color: "#4A90E2", mt: 3 }}
          >
            Relationships and Connection
          </Typography>
          <List
            dense
            sx={{
              listStyleType: "disc",
              pl: 4,
              color: "#333",
              textAlign: "left",
              mb: 3,
            }}
          >
            <ListItem sx={{ display: "list-item" }}>
              "I care deeply but can't show it the 'right' way"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "My partner doesn't understand my need for alone time"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "I info-dump about my interests and people get annoyed"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "Making friends as an adult feels impossible"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "I miss social cues and offend people without meaning to"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "Dating is confusing - so many unwritten rules"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "I want connection but socializing is exhausting"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "My family thinks autism is just an excuse"
            </ListItem>
          </List>

          <Typography
            variant="h6"
            gutterBottom
            sx={{ color: "#4A90E2", mt: 3 }}
          >
            The Mental Health Impact
          </Typography>
          <List
            dense
            sx={{
              listStyleType: "disc",
              pl: 4,
              color: "#333",
              textAlign: "left",
              mb: 3,
            }}
          >
            <ListItem sx={{ display: "list-item" }}>
              "Anxiety about doing things 'wrong' is constant"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "Depression from years of not fitting in"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "PTSD from bullying and rejection"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "Shame about my needs and differences"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "Imposter syndrome - am I 'autistic enough'?"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "Suicidal thoughts from the exhaustion of it all"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "Feeling like a burden to everyone"
            </ListItem>
          </List>

          <Typography
            variant="h6"
            gutterBottom
            sx={{ color: "#4A90E2", mt: 4 }}
          >
            What Neurodiversity-Affirming Support Looks Like
          </Typography>

          <Typography variant="body1" sx={{ mb: 2 }}>
            Dr. Rothenberg's approach honors your autistic identity and focuses
            on:
          </Typography>

          <List
            dense
            sx={{
              listStyleType: "disc",
              pl: 4,
              color: "#333",
              textAlign: "left",
              mb: 3,
            }}
          >
            <ListItem sx={{ display: "list-item" }}>
              <strong>Unmasking Safely:</strong> Learning when and how to drop
              the mask without losing everything you've built
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              <strong>Understanding Your Sensory Needs:</strong> Creating
              environments and routines that work WITH your nervous system
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              <strong>Recovering from Burnout:</strong> Rebuilding your capacity
              and learning to recognize early warning signs
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              <strong>Identity Exploration:</strong> Discovering who you are
              beneath years of masking and camouflaging
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              <strong>Advocacy Skills:</strong> Learning to ask for what you
              need without apologizing for being autistic
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              <strong>Relationship Navigation:</strong> Building connections
              that honor your authentic self
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              <strong>Workplace Strategies:</strong> Thriving professionally
              without sacrificing your wellbeing
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              <strong>Celebrating Your Strengths:</strong> Recognizing the gifts
              that come with your unique neurology
            </ListItem>
          </List>

          <Typography
            variant="h6"
            gutterBottom
            sx={{ color: "#4A90E2", mt: 3 }}
          >
            This Is NOT About:
          </Typography>

          <List
            dense
            sx={{
              listStyleType: "disc",
              pl: 4,
              color: "#333",
              textAlign: "left",
              mb: 3,
            }}
          >
            <ListItem sx={{ display: "list-item" }}>
              Teaching you to be "less autistic"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              Forcing eye contact or suppressing stims
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              Making you fit neurotypical standards
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              Treating autism as something to overcome
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              Ignoring your sensory needs
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              Dismissing your experiences
            </ListItem>
          </List>

          <Typography
            variant="body1"
            sx={{ mt: 3, fontWeight: "bold", color: "#4A90E2" }}
          >
            You deserve support that sees you, understands you, and helps you
            build a life where you can be authentically yourself. That's not too
            much to ask - it's the bare minimum you deserve.
          </Typography>
        </Box>
      </Container>
    </>
  );
};

export default AutismSupportForAdults;
